import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, inject } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { ReviewerService } from '../../services/reviewer.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { constants } from '../../../shared/constants/constants';
import { SkillsCriteria, SelectedCriteries, ReviewerCriteriaObject } from '../../../modules/reviewer/profile/profile.interface';
import { HeadingComponent } from '../heading/heading.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { PipesModule } from '../../pipes/pipes.module';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-skills',
  standalone: true,
  imports: [CommonModule, ButtonModule, FormsModule, FloatLabelModule, SkeletonModule, CalendarModule, InputTextModule, DropdownModule, HeadingComponent, MultiSelectModule, AccordionModule, TagModule, FileUploadModule, ChipModule, PipesModule],
  templateUrl: './add-skills.component.html',
  styleUrl: './add-skills.component.scss'
})
export class AddSkillsComponent implements OnInit {

  /**
   * MP-5736: the CHILD firm whose view of this reviewer we are editing, or ''
   * when there is none - which is every reviewer, every standalone firm and,
   * until the MP-5643 switcher lands, every parent-level FM. Sending '' means
   * "today's path", so this call is a no-op for all of them.
   */
  private readonly actingFirmUserService = inject(UserService);

  reviewerId!: string;
  userFirmId: string = '';
  isDataReady: boolean = false;
  skillsCriteriaListing: SkillsCriteria = [];
  preSelectedOptions: any = [];
  /**
   * MP-5722: experience-enabled values that were saved without a level, keyed
   * by criteria id and value. Set by validateExperienceLevels(), cleared when
   * the user picks a level or removes the chip.
   */
  readonly experienceLevelErrors = signal<Record<string, boolean>>({});

  constructor(private reviewerService: ReviewerService, 
    private ref: DynamicDialogRef, 
    public config: DynamicDialogConfig,
    private toast: ToastService
  ) { }

  ngOnInit() {
    this.userFirmId = this.config.data.firmId;
    this.reviewerId = this.config.data.userId;
    this.getUserSkillsByDynamicCriteria();
  }

  getUserSkillsByDynamicCriteria() {
    this.reviewerService.getFilteredCriteriaBasedUserSkills(this.reviewerId).subscribe((resp: any) => {
      this.reviewerService.getReviewerCriteria(this.reviewerId, this.actingFirmUserService.getActingChildFirmId()).subscribe((data: SelectedCriteries) => {
        let reviewerCriteriaObject: ReviewerCriteriaObject = {};
        if (data.length > 0) {
          reviewerCriteriaObject = data.reduce((obj, item) => {
            let wrap = {
              [item.criteriaName]: item
            };
            Object.assign(obj, wrap);
            return obj;
          }, {});
        }
        resp.map((d: any) => {
          const name = d.name;
          d.criteriaSelectedOption = reviewerCriteriaObject[name.toString()] ? reviewerCriteriaObject[name.toString()].criteriaSelectedOption : [];
          d.criteriaSelectedOption = this.getCriteriaOptions(d);
          this.preSelectedOptions.push(...d.criteriaSelectedOption);
        })
        this.skillsCriteriaListing = resp;
        this.isDataReady = true;
      })
    })
  }

  getCriteriaOptions(d: any){
    const options = d.criteriaSelectedOption.map((option: any) => {
      // Find the reference from label.values
      const filterData = d.label.values.find((value: any) => value.val === option.val);
      
      // Find the reference from experienceLevels if experience is enabled
      const experienceLevel = option.isExperienceEnabled 
        ? d.experienceLevels.find((exp: any) => exp.experience === option.selectedExperienceLevel?.experience) 
        : null;
    
      // If a match is found, return the original reference
      return filterData 
        ? Object.assign(filterData, { selectedExperienceLevel: experienceLevel || null }) 
        : null;
    }).filter((item: any) => item !== null);
    return options;
  }

  onRemoveChip(data: any, index: number) {
    const criteria: any = this.skillsCriteriaListing[index];
    const option = criteria.criteriaSelectedOption.filter((d: any) => {
      return d.val !== data.val;
    })
    criteria.criteriaSelectedOption = option;
    this.clearExperienceLevelError(criteria, data);
  }

  experienceLevelKey(criteria: any, option: any): string {
    return `${criteria?._id ?? criteria?.name}::${option?.val}`;
  }

  hasExperienceLevelError(criteria: any, option: any): boolean {
    return this.experienceLevelErrors()[this.experienceLevelKey(criteria, option)] === true;
  }

  onExperienceLevelChange(criteria: any, option: any): void {
    this.clearExperienceLevelError(criteria, option);
  }

  /**
   * MP-5722: an experience-enabled value must carry a chosen level. Nothing is
   * defaulted any more; the old `{experience: "", priority: "0"}` fallback is
   * what wrote the null / "0" priorities that drop users from experience
   * filtered searches.
   */
  validateExperienceLevels(): boolean {
    const errors: Record<string, boolean> = {};
    this.skillsCriteriaListing.forEach((criteria: any) => {
      (criteria.criteriaSelectedOption || []).forEach((option: any) => {
        if (option.isExperienceEnabled && !option.selectedExperienceLevel?.experience) {
          errors[this.experienceLevelKey(criteria, option)] = true;
        }
      });
    });
    this.experienceLevelErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  save(){
    if (!this.validateExperienceLevels()) {
      this.toast.showError('Required fields missing.');
      return;
    }
    let criterias:any = {};
    this.skillsCriteriaListing.map((d:any) => {
      if(!criterias[d.name]){
        criterias[d.name] = {
          selectedOptions: d.criteriaSelectedOption, 
          jobCategory: d.jobCategory.map((category: any) => category._id), 
          criteriaId: d._id
        };
      }
      if(d.criteriaSelectedOption.length){
        d.criteriaSelectedOption.forEach((item: any) => {
            let index = this.preSelectedOptions.findIndex((option: any) => option.val == item.val);
            if(index == -1){
              item.createdBy = constants.userRoles.firmManager;
            }
        });
      }
    })

    const payload = {
      criterias: criterias,
      reviewerId: this.reviewerId,
      // MP-5736: routes the write to this child's own records (MP-5734).
      actingFirmId: this.actingFirmUserService.getActingChildFirmId()
    }
    this.reviewerService.saveCriteriaReviewer(payload).subscribe((d:any)=>{
      this.onDiscard(true);
    });
  }

  onDiscard(param: boolean) {
    this.ref.close(param); 
  }

  private clearExperienceLevelError(criteria: any, option: any): void {
    const key = this.experienceLevelKey(criteria, option);
    if (this.experienceLevelErrors()[key]) {
      this.experienceLevelErrors.update((errors) => {
        const next = { ...errors };
        delete next[key];
        return next;
      });
    }
  }
}
